import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, Clock, Infinity as InfinityIcon } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function fmt(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

function timeLeft(exp) {
  if (!exp) return "Permanent";
  const diff = new Date(exp) - new Date();
  if (diff <= 0) return "Expiré";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}j ${hours}h restants`;
  return `${hours}h restants`;
}

export default function SanctionsPage() {
  const { data: bans = [], isLoading } = useQuery({
    queryKey: ["public-bans"],
    queryFn: () => api.get("/bans").then((r) => r.data),
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <header className="mb-8">
        <Badge variant="outline" className="border-primary/40 text-primary font-mono-tech uppercase tracking-widest mb-3">
          <ShieldAlert className="h-3 w-3 mr-1" /> Transparence
        </Badge>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">Sanctions actives</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Liste publique des joueurs actuellement sanctionnés sur RiverSide. Les sanctions expirées et révoquées ne sont pas affichées.
          Pour contester une sanction, ouvrez une demande dans la section « Support & demandes de déban » du forum.
        </p>
      </header>

      {isLoading ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : bans.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">Aucune sanction active. RiverSide est calme.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {bans.map((b) => (
            <Card key={b.id} className="hover:border-primary/60 transition-colors" data-testid={`ban-${b.id}`}>
              <CardContent className="p-5 grid sm:grid-cols-[1fr_auto] gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium">{b.persona_name || "Joueur inconnu"}</span>
                    <span className="font-mono-tech text-xs text-muted-foreground">{b.steam_id64}</span>
                    {b.source === "game" && (
                      <Badge variant="outline" className="border-primary/40 text-primary">En jeu</Badge>
                    )}
                    {b.permanent ? (
                      <Badge variant="destructive"><InfinityIcon className="h-3 w-3 mr-1" /> Permanent</Badge>
                    ) : (
                      <Badge variant="outline"><Clock className="h-3 w-3 mr-1" /> {timeLeft(b.expires_at)}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{b.reason}</p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>Par {b.issued_by}</p>
                  <p className="font-mono-tech">{fmt(b.issued_at)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
